// YTRedraftCapturePad — Re-draft assist drawer. Randy Feb 2026 (pt-yt-redraft-fix):
//   "Take a look at the 'Capture pad' in the Staging tab. It has a push to
//    silo button. Make this one do the same thing please."
// This version routes through the SAME DocSaveDialog + doc-commit flow the
// StagingTab uses — no more custom /docs/paste call that was silently
// throwing "Field required". Also fixed mobile positioning: was
// items-end sm:items-center → now items-center everywhere so it stays
// vertically centered on phones instead of dropping to the bottom bezel.
//
// Feb 27 2026 (Randy pt-ytre-guide-this-reply) — second action:
// "Guide this reply". Same textarea, different destination: instead of
// pushing to the silo's permanent RAG memory, the text is returned via
// onClose(didPush, guidance, persist) so the parent card can inject it
// into the SYSTEM PROMPT of the next re-draft. `persist` = true keeps
// the guidance armed on the card for future re-drafts (until the user
// clears or edits it); false = one-shot, clears after next re-draft.
// Chip on the card advertises the armed state now (Randy revised —
// previously invisible, but he wanted a visual "your guidance is
// affecting this draft" reassurance).
//
// Optional props for edit mode:
//   initialText     — pre-fills the textarea with the card's active
//                     guidance so the user can edit instead of retype.
//   initialPersist  — pre-fills the sticky checkbox.
import { useEffect, useRef, useState } from "react";
import { X, Microphone, MicrophoneSlash, PaperPlaneRight, Compass } from "@phosphor-icons/react";
import { toast } from "sonner";
import DocSaveDialog from "@/components/DocSaveDialog";

export default function YTRedraftCapturePad({ siloId, onClose, initialText = "", initialPersist = false }) {
  const [text, setText] = useState(initialText || "");
  const [persist, setPersist] = useState(!!initialPersist);
  const [listening, setListening] = useState(false);
  const [didPush, setDidPush] = useState(false);
  const [commitState, setCommitState] = useState({ open: false });
  const recognitionRef = useRef(null);
  // Guidance the user submitted via "Guide this reply". Held here so the
  // pad can pass it back up on close.
  const guidanceRef = useRef(initialText || "");
  const persistRef = useRef(!!initialPersist);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !commitState.open) onClose(didPush, guidanceRef.current || null, persistRef.current); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, didPush, commitState.open]);

  const toggleVoice = () => {
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice dictation isn't supported in this browser — type instead.");
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    rec.onresult = (ev) => {
      let finalText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) finalText += ev.results[i][0].transcript + " ";
      }
      if (finalText) setText((t) => (t ? `${t} ${finalText}` : finalText).trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const openPushDialog = () => {
    const body = text.trim();
    if (!body) return toast.error("Nothing to push — dictate or type first.");
    const title = body.split("\n")[0].slice(0, 80) || "YT re-draft note";
    setCommitState({ open: true, text: body, defaultTitle: title });
  };

  // Feb 27 2026 (Randy pt-ytre-guide-this-reply) — capture-only path.
  // No silo write, no RAG index bump. Text is handed back to the parent
  // via onClose so it can be injected into the NEXT re-draft's system
  // prompt. When `persist=true`, parent keeps the guidance armed on the
  // card for future re-drafts (edit or clear from the chip); false =
  // one-shot, clears after next re-draft.
  const submitGuidance = () => {
    const body = text.trim();
    if (!body) return toast.error("Type or dictate the guidance first.");
    guidanceRef.current = body;
    persistRef.current = persist;
    toast.success(persist
      ? "Guidance armed — will keep shaping this card's re-drafts until you clear it."
      : "Guidance armed — will shape the next re-draft once.");
    onClose(didPush, body, persist);
  };

  return (
    <>
      <div className="fixed inset-0 z-[220] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="yt-redraft-capturepad">
        <div className="w-full max-w-lg bg-[#FDFCF8] border hairline rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b hairline">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-medium">Re-draft assist</div>
              <div className="text-[14px] font-medium text-[#1C1B1A]">{initialText ? "Edit guidance" : "Capture new info for the silo"}</div>
            </div>
            <button onClick={() => onClose(didPush, guidanceRef.current || null, persistRef.current)} data-testid="yt-redraft-close" className="p-1 rounded hover:bg-[#F0F1EE]">
              <X size={16} />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-[11.5px] text-[#6B6A68] leading-relaxed">
              Dictate a paragraph or two, then either <strong>push it to the silo</strong> (permanent memory) or use <strong>Guide this reply</strong> to shape re-drafts of this card only.
            </p>
            <div className="relative">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                placeholder="Speak or type. Anything you add here becomes new silo memory."
                data-testid="yt-redraft-textarea"
                className="w-full bg-white border hairline rounded-md p-3 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
              <button onClick={toggleVoice} data-testid="yt-redraft-voice"
                className={`absolute right-2 bottom-2 p-2 rounded-full ${listening ? "bg-[#B54848] text-white animate-pulse" : "bg-[#F0F1EE] text-[#4A5D4E] hover:bg-[#E4E6E1]"}`}
                title={listening ? "Stop dictation" : "Start dictation"}>
                {listening ? <MicrophoneSlash size={14} weight="bold" /> : <Microphone size={14} weight="bold" />}
              </button>
            </div>
            {/* Feb 27 2026 (Randy pt-ytre-guide-persist) — sticky toggle
                lets the guidance apply to EVERY subsequent re-draft of
                this card until the user edits or clears it. Off = one-
                shot (clears after next re-draft). Only relevant for the
                "Guide this reply" action — silo push is always permanent. */}
            <label className="flex items-center gap-2 text-[11.5px] text-[#4A5D4E] cursor-pointer select-none" data-testid="yt-redraft-persist-toggle-row">
              <input
                type="checkbox"
                checked={persist}
                onChange={(e) => setPersist(e.target.checked)}
                data-testid="yt-redraft-persist-toggle"
                className="h-3.5 w-3.5 rounded border-[#C4C6C0] text-[#4A5D4E] focus:ring-[#4A5D4E]"
              />
              <span>Keep guidance armed for future re-drafts of this card <span className="text-[#8A8C86]">(you can edit or clear from the chip)</span></span>
            </label>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <button onClick={openPushDialog} disabled={!text.trim()}
                data-testid="yt-redraft-push"
                className="text-[12px] px-3 py-1.5 rounded-md bg-[#B54848] text-white hover:bg-[#8f3838] disabled:opacity-50 flex items-center gap-1.5 font-medium">
                <PaperPlaneRight size={12} weight="bold" /> Push to silo
              </button>
              <button onClick={submitGuidance} disabled={!text.trim()}
                data-testid="yt-redraft-guide"
                title="Use this text to guide this card's re-draft. Not saved to silo memory."
                className="text-[12px] px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5 font-medium">
                <Compass size={12} weight="bold" /> Guide this reply
              </button>
              {didPush && (
                <span className="text-[11px] text-[#2F6B3A] font-medium" data-testid="yt-redraft-pushed-indicator">✓ Pushed — close to arm Re-draft</span>
              )}
              <button onClick={() => onClose(didPush, guidanceRef.current || null, persistRef.current)} className="text-[11px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] ml-auto" data-testid="yt-redraft-cancel">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Uses the SAME robust flow the Staging tab's Push-to-Silo uses:
          title input, duplicate-name guard, tier selector, oversize check
          → split. No more silent "Field required" errors. */}
      <DocSaveDialog
        open={commitState.open}
        onClose={() => setCommitState({ open: false })}
        content={commitState.text || ""}
        sourceSiloId={siloId}
        sourceDoc={null}
        silos={[{ id: siloId, name: "this silo" }]}
        defaultTitle={commitState.defaultTitle || ""}
        initialTier={3}
        dialogTitle="Push to silo"
        onSaved={() => {
          setDidPush(true);
          setCommitState({ open: false });
          toast.success("Pushed to silo. Close to arm Re-draft.");
        }}
      />
    </>
  );
}
